import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "./components/Header";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Gallery from "./components/Gallery";
import ProductDialog from "./components/ProductDialog";
import About from "./components/About";
import CustomSection from "./components/CustomSection";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import SiteMeta from "./components/SiteMeta";

import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import { useProducts } from "./useProducts";

const renderBlock = (block, idx, products, setSelected) => {
  if (!block.enabled) return null;
  const key = block.id || `${block.type}-${idx}`;
  switch (block.type) {
    case "hero":
      return <Hero key={key} />;
    case "marquee":
      return <Marquee key={key} />;
    case "gallery":
      return <Gallery key={key} products={products} onSelect={setSelected} />;
    case "about":
      return <About key={key} />;
    case "contact":
      return <Contact key={key} />;
    case "custom":
      return <CustomSection key={key} section={block} index={idx} />;
    default:
      return null;
  }
};

const MaintenanceBanner = () => (
  <div
    data-testid="maintenance-admin-banner"
    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 bg-[color:var(--tr-ink)] text-[color:var(--tr-bg)] font-sub text-[0.7rem] uppercase tracking-[0.22em] shadow-lg"
  >
    Lavori in corso attivi — i visitatori vedono solo la schermata iniziale
  </div>
);

const Home = () => {
  const { settings, loaded } = useSettings();
  const { user } = useAuth();
  const maintenance = settings.maintenance_mode === true && !user;
  const { products, error } = useProducts();
  const [selected, setSelected] = useState(null);

  // Ensure every page load / reload starts from the Hero (top of the page).
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);

  const blocks =
    settings.page_blocks && settings.page_blocks.length
      ? settings.page_blocks
      : [
          { id: "hero", type: "hero", enabled: true },
          { id: "marquee", type: "marquee", enabled: true },
          { id: "gallery", type: "gallery", enabled: true },
          { id: "about", type: "about", enabled: true },
          { id: "contact", type: "contact", enabled: true },
        ];

  // Keep selected product fresh if it changes in the backend
  useEffect(() => {
    if (!selected) return;
    const updated = products.find((p) => p.id === selected.id);
    if (updated && JSON.stringify(updated) !== JSON.stringify(selected)) {
      setSelected(updated);
    }
  }, [products, selected]);

  if (!loaded) return <main data-testid="home-page" className="h-screen" />;

  if (maintenance) {
    return (
      <main data-testid="home-page" className="h-screen overflow-hidden">
        <Header maintenance />
        <Hero maintenance />
      </main>
    );
  }

  return (
    <main data-testid="home-page">
      {settings.maintenance_mode === true && user && <MaintenanceBanner />}
      <Header />
      {error && (
        <div className="px-6 sm:px-12 py-16 text-center font-body text-[color:var(--tr-ink-soft)]">
          {error}
        </div>
      )}
      {blocks.map((b, i) => renderBlock(b, i, products, setSelected))}
      {settings.footer_enabled !== false && <Footer />}
      <ProductDialog product={selected} onClose={() => setSelected(null)} />
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="bottom-center" theme="light" />
      <BrowserRouter>
        <SettingsProvider>
          <SiteMeta />
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </AuthProvider>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
